// src/slices/orderSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { CART_CLEAR_ITEMS } from '../constants/cartConstants';
import { getAxiosConfig } from '../utils/http';
// Removed unused ORDER_LIST_MY_RESET and resetUserState imports
// import { ORDER_LIST_MY_RESET } from '../constants/orderConstants';
// import { resetUserState } from './userSlice';
import { logout } from './userSlice'; // so we can dispatch logout on token failure

const normalizeOrders = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.orders)) return payload.orders;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.orders)) return payload.data.orders;
  return [];
};


// Create Order
export const createOrder = createAsyncThunk(
  'order/createOrder',
  async ({ stepUpToken, ...order }, { getState, dispatch, rejectWithValue }) => {
    try {
  const state = getState();
  const config = getAxiosConfig(state, { withCredentials: true });
  // Order placement is step-up-gated for non-guest sessions (backend
  // exempts guests entirely, since they have no password to re-verify).
  if (stepUpToken) config.headers['x-step-up-token'] = stepUpToken;
  const { data } = await axios.post('/api/orders', order, config);

      // Clear cart after order success
      dispatch({ type: CART_CLEAR_ITEMS });
      localStorage.removeItem('cartItems');

      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get Order Details
export const getOrderDetails = createAsyncThunk(
  'order/getOrderDetails',
  async (id, { getState, rejectWithValue }) => {
    try {
  const state = getState();
  const config = getAxiosConfig(state, { withCredentials: true, forbidAuthForGuest: true });
  const { data } = await axios.get(`/api/orders/${id}`, config);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Pay Order
export const payOrder = createAsyncThunk(
  'order/payOrder',
  async ({ orderId, paymentResult }, { getState, rejectWithValue }) => {
    try {
  const state = getState();
  const config = getAxiosConfig(state, { withCredentials: true, forbidAuthForGuest: true });
  const { data } = await axios.put(`/api/orders/${orderId}/pay`, paymentResult, config);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Pay Order (Stripe)
export const payOrderStripe = createAsyncThunk(
  'order/payOrderStripe',
  async ({ orderId, paymentResult }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state, { withCredentials: true, forbidAuthForGuest: true });
      const { data } = await axios.put(`/api/orders/${orderId}/pay/stripe-session`, paymentResult, config);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Deliver Order
export const deliverOrder = createAsyncThunk(
  'order/deliverOrder',
  async ({ orderId, message }, { getState, rejectWithValue }) => {
    try {
  const state = getState();
  const config = getAxiosConfig(state, { withCredentials: true });
  const { data } = await axios.put(`/api/orders/${orderId}/deliver`, { message }, config);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Ship Order
export const shipOrder = createAsyncThunk(
  'order/shipOrder',
  async (orderId, { getState, rejectWithValue }) => {
    try {
  const state = getState();
  const config = getAxiosConfig(state, { withCredentials: true });
  const { data } = await axios.put(`/api/orders/${orderId}/ship`, {}, config);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Admin: mark order processed
export const markOrderProcessed = createAsyncThunk(
  'order/markOrderProcessed',
  async (orderId, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state, { withCredentials: true });
      const { data } = await axios.put(`/api/orders/${orderId}/processed`, {}, config);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// User/Guest: submit cancellation request
export const submitCancelRequest = createAsyncThunk(
  'order/submitCancelRequest',
  async ({ orderId, reason, description }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state, { withCredentials: true, forbidAuthForGuest: true });
      const payload = { reason, description };
      const { data } = await axios.post(`/api/orders/${orderId}/cancel-request`, payload, config);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Admin: approve/deny cancellation request
export const decideCancelRequest = createAsyncThunk(
  'order/decideCancelRequest',
  async ({ orderId, decision, note }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state, { withCredentials: true });
      const payload = { decision, note };
      const { data } = await axios.put(`/api/orders/${orderId}/cancel-request/decision`, payload, config);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// User/Guest: submit refund request (delivered only)
export const submitRefundRequest = createAsyncThunk(
  'order/submitRefundRequest',
  async ({ orderId, reason, description, items, refundAmount }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state, { withCredentials: true, forbidAuthForGuest: true });
      const payload = { reason, description, items, refundAmount };
      const { data } = await axios.post(`/api/orders/${orderId}/refund-request`, payload, config);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Admin: approve refund request
export const approveRefundRequest = createAsyncThunk(
  'order/approveRefundRequest',
  async ({ orderId, refundAmount, items, adminNote, stepUpToken }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state, { withCredentials: true });
      if (stepUpToken) config.headers['x-step-up-token'] = stepUpToken;
      const payload = { refundAmount, items, adminNote };
      const { data } = await axios.put(`/api/orders/${orderId}/refund-request/approve`, payload, config);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Admin: deny refund request
export const denyRefundRequest = createAsyncThunk(
  'order/denyRefundRequest',
  async ({ orderId, reason, stepUpToken }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state, { withCredentials: true });
      if (stepUpToken) config.headers['x-step-up-token'] = stepUpToken;
      const payload = { reason };
      const { data } = await axios.put(`/api/orders/${orderId}/refund-request/deny`, payload, config);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Admin: initiate refund (Stripe/PayPal)
export const initiateRefund = createAsyncThunk(
  'order/initiateRefund',
  async ({ orderId, message, stepUpToken }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state, { withCredentials: true });
      if (stepUpToken) config.headers['x-step-up-token'] = stepUpToken;
      const { data } = await axios.put(`/api/orders/${orderId}/refund/initiate`, { message }, config);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Admin: cancel order directly (no request needed)
export const adminCancelOrder = createAsyncThunk(
  'order/adminCancelOrder',
  async ({ orderId, message }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const config = getAxiosConfig(state, { withCredentials: true });
      const { data } = await axios.put(
        `/api/orders/${orderId}/admin-cancel`,
        { message },
        config
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to cancel order'
      );
    }
  }
);

// Async thunk: list my orders
export const listMyOrders = createAsyncThunk(
  'order/listMyOrders',
  async (_, { getState, rejectWithValue, dispatch }) => {
    try {
  const state = getState();
  const config = getAxiosConfig(state, { withCredentials: true });
  const { data } = await axios.get('/api/orders/myorders', config);

      return data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      if (message === 'Not authorized, token failed') {
        dispatch(logout());
      }
      return rejectWithValue(message);
    }
  }
);

// List All Orders (Admin)
export const listOrders = createAsyncThunk(
  'order/listOrders',
  async (_, { getState, rejectWithValue }) => {
    try {
  const state = getState();
  const config = getAxiosConfig(state, { withCredentials: true });
  const { data } = await axios.get('/api/orders', config);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Slice
const orderSlice = createSlice({
  name: 'order',
  initialState: {
    order: {},
    orderDetails: {},
    orders: [],
    myOrders: [],
    loading: false,
    error: null,
    success: false,
    successPay: false,
    successDeliver: false,
    successShip: false,

    loadingProcess: false,
    successProcess: false,
    loadingCancelRequest: false,
    successCancelRequest: false,
    loadingCancelDecision: false,
    successCancelDecision: false,
    loadingRefundRequest: false,
    successRefundRequest: false,
    loadingRefundInitiate: false,
    successRefundInitiate: false,
  },
  reducers: {
    resetOrderState: (state) => {
      state.order = {};
      state.orderDetails = {};
      state.orders = [];
      state.myOrders = [];
      state.loading = false;
      state.error = null;
      state.success = false;
      state.successPay = false;
      state.successDeliver = false;
      state.successShip = false;

      state.loadingProcess = false;
      state.successProcess = false;
      state.loadingCancelRequest = false;
      state.successCancelRequest = false;
      state.loadingCancelDecision = false;
      state.successCancelDecision = false;
      state.loadingRefundRequest = false;
      state.successRefundRequest = false;
      state.loadingRefundInitiate = false;
      state.successRefundInitiate = false;
    },
    // New: reset only the success flags so effects won't loop
    resetOrderFlags: (state) => {
      state.successPay = false;
      state.successDeliver = false;
      state.successShip = false;

      state.successProcess = false;
      state.successCancelRequest = false;
      state.successCancelDecision = false;
      state.successRefundRequest = false;
      state.successRefundInitiate = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Order
      .addCase(createOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.order = action.payload;
        state.success = true;
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get Order Details
      .addCase(getOrderDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOrderDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.orderDetails = action.payload;
      })
      .addCase(getOrderDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Pay Order
      .addCase(payOrder.pending, (state) => {
        state.loadingPay = true;
      })
      .addCase(payOrder.fulfilled, (state, action) => {
        state.loadingPay = false;
        state.successPay = true;
        state.orderDetails = action.payload;
      })
      .addCase(payOrder.rejected, (state, action) => {
        state.loadingPay = false;
        state.error = action.payload;
      })

      // Deliver Order
      .addCase(deliverOrder.pending, (state) => {
        state.loadingDeliver = true;
      })
      .addCase(deliverOrder.fulfilled, (state, action) => {
        state.loadingDeliver = false;
        state.successDeliver = true;
        state.orderDetails = action.payload;
      })
      .addCase(deliverOrder.rejected, (state, action) => {
        state.loadingDeliver = false;
        state.error = action.payload;
      })

      // Ship Order
      .addCase(shipOrder.pending, (state) => {
        state.loadingShip = true;
      })
      .addCase(shipOrder.fulfilled, (state, action) => {
        state.loadingShip = false;
        state.successShip = true;
        state.orderDetails = action.payload;
      })
      .addCase(shipOrder.rejected, (state, action) => {
        state.loadingShip = false;
        state.error = action.payload;
      })

      // Mark Processed (Admin)
      .addCase(markOrderProcessed.pending, (state) => {
        state.loadingProcess = true;
        state.error = null;
      })
      .addCase(markOrderProcessed.fulfilled, (state, action) => {
        state.loadingProcess = false;
        state.successProcess = true;
        state.orderDetails = action.payload;
      })
      .addCase(markOrderProcessed.rejected, (state, action) => {
        state.loadingProcess = false;
        state.error = action.payload;
      })

      // Submit Cancel Request (User/Guest)
      .addCase(submitCancelRequest.pending, (state) => {
        state.loadingCancelRequest = true;
        state.error = null;
      })
      .addCase(submitCancelRequest.fulfilled, (state, action) => {
        state.loadingCancelRequest = false;
        state.successCancelRequest = true;
        state.orderDetails = action.payload;
      })
      .addCase(submitCancelRequest.rejected, (state, action) => {
        state.loadingCancelRequest = false;
        state.error = action.payload;
      })

      // Decide Cancel Request (Admin)
      .addCase(decideCancelRequest.pending, (state) => {
        state.loadingCancelDecision = true;
        state.error = null;
      })
      .addCase(decideCancelRequest.fulfilled, (state, action) => {
        state.loadingCancelDecision = false;
        state.successCancelDecision = true;
        state.orderDetails = action.payload;
      })
      .addCase(decideCancelRequest.rejected, (state, action) => {
        state.loadingCancelDecision = false;
        state.error = action.payload;
      })

      // Submit Refund Request (User/Guest)
      .addCase(submitRefundRequest.pending, (state) => {
        state.loadingRefundRequest = true;
        state.error = null;
      })
      .addCase(submitRefundRequest.fulfilled, (state, action) => {
        state.loadingRefundRequest = false;
        state.successRefundRequest = true;
        state.orderDetails = action.payload;
      })
      .addCase(submitRefundRequest.rejected, (state, action) => {
        state.loadingRefundRequest = false;
        state.error = action.payload;
      })

      // Initiate Refund (Admin)
      .addCase(initiateRefund.pending, (state) => {
        state.loadingRefundInitiate = true;
        state.error = null;
      })
      .addCase(initiateRefund.fulfilled, (state, action) => {
        state.loadingRefundInitiate = false;
        state.successRefundInitiate = true;
        state.orderDetails = action.payload;
      })
      .addCase(initiateRefund.rejected, (state, action) => {
        state.loadingRefundInitiate = false;
        state.error = action.payload;
      })
      // Admin Cancel Order
      .addCase(adminCancelOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(adminCancelOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orderDetails = action.payload;
      })
      .addCase(adminCancelOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(payOrderStripe.pending, (state) => {
        state.loadingPay = true;
        state.error = null;
      })
      .addCase(payOrderStripe.fulfilled, (state, action) => {
        state.loadingPay = false;
        state.successPay = true;
        state.orderDetails = action.payload;
      })
      .addCase(payOrderStripe.rejected, (state, action) => {
        state.loadingPay = false;
        state.error = action.payload;
      })
      // List My Orders
      .addCase(listMyOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(listMyOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.myOrders = action.payload;
      })
      .addCase(listMyOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // List Orders (Admin)
      .addCase(listOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(listOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = normalizeOrders(action.payload);
      })
      .addCase(listOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
  },
});

export const { resetOrderState, resetOrderFlags } = orderSlice.actions;

export default orderSlice.reducer;

// export {
//   createOrder,
//   getOrderDetails,
//   payOrder,
//   deliverOrder,
//   shipOrder,
//   listMyOrders,
//   listOrders,
// };
